/**
 * Fixture song for MUS-522: a short piano piece split into one phrase per line,
 * in the shape the line splitter produces (one part per hand, each line its own
 * MusicXML document, ties kept at note level). Ties cross both line breaks:
 *
 *   line 1  RH  C4 D4 E4 G4~      LH  C3~ (whole)
 *   line 2  RH  ~G4 A4 B4 C5~     LH  ~C3 (whole)
 *   line 3  RH  ~C5~ (half) ~C5 D5   LH  F3 (whole)
 *
 * So line 2 opens with two continuations (RH G4, LH C3), and line 3 opens with
 * a continuation that ties on again (C5 half, then C5 quarter). Line 1's tie
 * starts dangle: their stops are in the next document.
 */

type FixtureNote = {
  step: string;
  octave: number;
  /** In divisions (1 division = 1 quarter). */
  duration: number;
  type: string;
  /** Tie types in written order, e.g. ['stop', 'start']. */
  ties?: ('start' | 'stop')[];
};

function noteXml(note: FixtureNote): string {
  const ties = note.ties ?? [];
  return [
    '<note>',
    `<pitch><step>${note.step}</step><octave>${note.octave}</octave></pitch>`,
    `<duration>${note.duration}</duration>`,
    ...ties.map((type) => `<tie type="${type}"/>`),
    '<voice>1</voice>',
    `<type>${note.type}</type>`,
    ties.length > 0
      ? `<notations>${ties.map((type) => `<tied type="${type}"/>`).join('')}</notations>`
      : '',
    '</note>',
  ].join('');
}

function partXml(id: string, clef: 'G' | 'F', notes: FixtureNote[]): string {
  const clefLine = clef === 'G' ? 2 : 4;
  return [
    `<part id="${id}"><measure number="1">`,
    '<attributes><divisions>1</divisions><key><fifths>0</fifths></key>',
    '<time><beats>4</beats><beat-type>4</beat-type></time>',
    `<clef><sign>${clef}</sign><line>${clefLine}</line></clef></attributes>`,
    ...notes.map(noteXml),
    '</measure></part>',
  ].join('');
}

function lineXml(rightHand: FixtureNote[], leftHand: FixtureNote[]): string {
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<score-partwise version="3.1"><part-list>',
    '<score-part id="P1"><part-name>Piano RH</part-name></score-part>',
    '<score-part id="P2"><part-name>Piano LH</part-name></score-part>',
    '</part-list>',
    partXml('P1', 'G', rightHand),
    partXml('P2', 'F', leftHand),
    '</score-partwise>',
  ].join('');
}

const quarter = (step: string, octave: number, ties?: FixtureNote['ties']): FixtureNote => ({
  step,
  octave,
  duration: 1,
  type: 'quarter',
  ties,
});

export const TIED_ACROSS_LINE_BREAKS: string[] = [
  lineXml(
    [quarter('C', 4), quarter('D', 4), quarter('E', 4), quarter('G', 4, ['start'])],
    [{ step: 'C', octave: 3, duration: 4, type: 'whole', ties: ['start'] }],
  ),
  lineXml(
    [quarter('G', 4, ['stop']), quarter('A', 4), quarter('B', 4), quarter('C', 5, ['start'])],
    [{ step: 'C', octave: 3, duration: 4, type: 'whole', ties: ['stop'] }],
  ),
  lineXml(
    [
      { step: 'C', octave: 5, duration: 2, type: 'half', ties: ['stop', 'start'] },
      quarter('C', 5, ['stop']),
      quarter('D', 5),
    ],
    [{ step: 'F', octave: 3, duration: 4, type: 'whole' }],
  ),
];

/** The same song as ONE phrase (no line breaks), for comparing with a within-phrase tie. */
export const TIED_ACROSS_LINE_BREAKS_AS_ONE_LINE: string = (() => {
  const measures = (part: 'P1' | 'P2') =>
    TIED_ACROSS_LINE_BREAKS.map((line, index) => {
      const body = line.split(`<part id="${part}">`)[1].split('</part>')[0];
      return body.replace('<measure number="1">', `<measure number="${index + 1}">`);
    }).join('');
  return [
    '<?xml version="1.0" encoding="UTF-8"?>',
    '<score-partwise version="3.1"><part-list>',
    '<score-part id="P1"><part-name>Piano RH</part-name></score-part>',
    '<score-part id="P2"><part-name>Piano LH</part-name></score-part>',
    '</part-list>',
    `<part id="P1">${measures('P1')}</part>`,
    `<part id="P2">${measures('P2')}</part>`,
    '</score-partwise>',
  ].join('');
})();
